/**
 * The funnel as a funnel: how many got AT LEAST this far.
 *
 * ★ THE STATS API COUNTS EACH CONTACT ONCE, IN THE STATE IT IS IN NOW. A
 * contact who replied is `REPLIED` and nothing else — not also "delivered",
 * not also "read". Drawn as bars per state, that is a distribution, and it
 * misleads in exactly the way a campaign that is going WELL is misleading:
 * every reply makes the "Delivered" bar shorter. A funnel has to be
 * cumulative, and this is the one place the arithmetic lives — the Overview's
 * "Delivered" tile already did `DELIVERED + READ + REPLIED` by hand, which is
 * how the two came to disagree once.
 *
 * Pure and import-free so it can be proven without a store or the API layer.
 *
 * @param {Record<string, number>} funnel   per-state counts from the stats API
 * @param {number} attempted                `totals.attempted` — includes failures
 * @returns {Array<{key, label, count, share, drop}>} top to bottom; `share` is of
 *   the widest stage, `drop` is how many did not make it from the stage above
 */
export function cumulativeFunnel(funnel = {}, attempted = 0) {
	const n = (k) => Number(funnel?.[k] ?? 0) || 0;

	const replied = n('REPLIED');
	const read = replied + n('READ');
	const delivered = read + n('DELIVERED');
	const sent = delivered + n('SENT');
	const handed = sent + n('SUBMITTED');
	// A stale `attempted` can lag the rows it was counted from; the funnel
	// must never draw a lower stage wider than the one above it.
	const base = Math.max(Number(attempted) || 0, handed);

	const stages = [
		{ key: 'attempted', label: 'Attempted', count: base },
		{ key: 'handed', label: 'Handed to WhatsApp', count: handed },
		{ key: 'sent', label: 'Sent', count: sent },
		{ key: 'delivered', label: 'Delivered', count: delivered },
		{ key: 'read', label: 'Read', count: read },
		{ key: 'replied', label: 'Replied', count: replied },
	];

	return stages.map((stage, i) => ({
		...stage,
		share: base ? stage.count / base : 0,
		drop: i ? stages[i - 1].count - stage.count : 0,
	}));
}

/** `0.0%`, from a fraction. */
export const pctOf = (fraction) => `${(Number(fraction || 0) * 100).toFixed(1)}%`;
